import { createContext, useContext, useEffect, useState } from "react";
import * as chatServices from "../services/chats";
import { useAuthContext } from "./auth-context";
import { TChatListResponse, TChatResponse } from "../lib/types/chats";
import { TUserInfo } from "../lib/types/users";
import { TMessage } from "../lib/types/general";
import { Modal } from "../components/common/modal";
import { useFileManagement } from "../services/file-management";
import { ASSETS_ROUTES } from "../lib/common/constants";
import { getFirebaseAppAuth } from "../services/users";
import { sendEmailVerification as FBSendEmailVerification } from "@firebase/auth";

export type TWSReadNotificationData = {
    fromDate: string;
    toDate: string;
    conversationId: number;
};
export type TGetChats = (conversationId: number, lastMessageDateNoTZ?: string) => Promise<TChatResponse>;
export type TGetChatsWithUser = (withUserId: number, lastMessageDateNoTZ?: string) => Promise<TChatResponse>;
export type TSendChat = (content: string, conversationId: number) => Promise<any>;
export type TUserToChatInfo = {
    id: number;
    name: string;
};
export type TGetChatList = (meId: number, pageNumber: number, is_archived: boolean) => Promise<TChatListResponse>;

export type TSendToArchive = (conversation_id: number, setArchive: boolean) => Promise<void>;

type TReadNotificationsMessageHandler = (p: TWSReadNotificationData) => void;
export type TUseServiceContext = {
    security: {
        isUserEmailVerified: () => Promise<boolean>;
        sendEmailVerification: () => void;
        userPhoneNeedsVerification: () => boolean;
    };
    general: {
        logOut: () => void;
    };
    notifications: {
        showPopup: (message: string, title: string) => void;
        hidePopup: () => void;
        showNotificationsPopup: boolean;
        notificationsPopupMessage: string;
        notificationsPopupTitle: string;
        notificationsGetLogoURL: (message: TMessage) => string;
        notificationMessageClickHandler: (message: TMessage) => void;
        addNewMessagesHandler: (fn: (() => void) | undefined) => void;
        removeMessageHandler: (fn: () => void) => void;
        addNewConversationMessageHandler: (fn: ((conversationId: number) => void) | undefined) => void;
        removeNewConversationMessageHandler: (fn: (conversationId: number) => void) => void;
        setReadNotificationMessageHandler: (fn?: TReadNotificationsMessageHandler) => void;
    };
    chats: {
        openCloseMessageCenter: () => void;
        addOpenCloseMessageCenterHandler: (fn: () => void) => void;
        removeOpenCloseMessageCenterHandler: (fn: () => void) => void;
        removeUserIdToChat: (userId: number) => void;
        getChats: TGetChats;
        sendMessage: TSendChat;
        startConversation: TGetChatsWithUser;
        endConversation: TGetChatsWithUser;
        getMessages: TGetChats;
        //setShowChat: (v: boolean) => void;
        listUserIdToChat: TUserToChatInfo[];
        addUserIdToChat: (v: TUserToChatInfo) => void;
        removeSocket: () => void;
        getChatList: TGetChatList;
        sendToArchive: TSendToArchive;
    };
};

declare global {
    interface Window {
        wwgWS: WebSocket | undefined;
    }
}

window.wwgWS = undefined;

const removeSocket = () => {
    if (window.wwgWS) {
        const ws = window.wwgWS as WebSocket;
        if (ws.readyState === 0) {
            setTimeout(() => {
                removeSocket();
            }, 500);
        }

        if (ws.readyState === 1) {
            ws.close();
            delete window.wwgWS;
        }
    }
};

export const useService = (): TUseServiceContext => {
    const { getUserTokenId, userInfo, logOutUser, isUserEmailVerified } = useAuthContext();
    const { getImagePath } = useFileManagement();
    const [listUserIdToChat, _setListUserIdToChat] = useState<TUserToChatInfo[]>([]);
    const [newConversationMessageHandler, _setNewConversationMessageHandler] = useState<((conversationId: number) => void)[]>();
    const [newMessagesHandler, _setNewMessagesHandler] = useState<(() => void)[]>();

    const userSendEmailVerification = async () => {
        const auth = getFirebaseAppAuth();
        if (!auth.currentUser) {
            setTimeout(() => {
                userSendEmailVerification();
            }, 1000);
            return;
        }
        return FBSendEmailVerification(auth.currentUser);
    };

    const addNewConversationMessageHandler = (fn: ((_v: number) => void) | undefined) => {
        _setNewConversationMessageHandler((old) => {
            const newList = old ? [...old] : [];
            if (fn) {
                newList.push(fn);
                return newList;
            }
            return old;
        });
    };
    const addNewMessagesHandler = (fn: (() => void) | undefined) => {
        _setNewMessagesHandler((old) => {
            if (old && old.findIndex((h) => h === fn) !== -1) return old;

            const newList = old ? [...old] : [];
            if (fn) {
                newList.push(fn);
                return newList;
            }
            return old;
        });
    };

    const addUserIdToChat = (user: TUserToChatInfo) => {
        //the max chats to show is the width of the screen divided by 400px (aprox the with
        //of every chat window plus spaces, this is a thumb rule), minimun 1 chat
        const MAX_CHATS_TO_SHOW = Math.max(1, Math.floor(window.innerWidth / 400 - 1)); //  3;

        //if the user shrinks down the screen and we had
        //more chats showing than what the screen can show
        //then we remove chats until the list length is MAX_CHATS_TO_SHOW
        const cleanupIfScreenGotSmaller = () => {
            if (listUserIdToChat.length > MAX_CHATS_TO_SHOW) {
                listUserIdToChat.shift();
                cleanupIfScreenGotSmaller();
            }
        };

        cleanupIfScreenGotSmaller();
        if (listUserIdToChat.findIndex((u) => u.id === user.id) !== -1) return;
        //we remove the first element if we reached max chats shown
        if (listUserIdToChat.length >= MAX_CHATS_TO_SHOW) listUserIdToChat.shift();

        _setListUserIdToChat([...listUserIdToChat, user]);
    };

    const hidePopup = () => {
        setNotificationsPopupMessage("");
        setNotificationsPopupTitle("");
        setShowNotificationsPopup(false);
    };
    const removeUserIdToChat = (userId: number) => {
        const newList = listUserIdToChat.filter((u) => u.id !== userId);
        _setListUserIdToChat([...newList]);
    };

    const removeMessageHandler = (fn: () => void) => {
        _setNewMessagesHandler((old) => {
            if (!old) return old;
            const handlerIndex = old.findIndex((f) => f === fn);

            if (handlerIndex === -1) return old;
            old.splice(handlerIndex, 1);
            const newList = [...old];
            return newList;
        });
    };

    const removeNewConversationMessageHandler = (fn: (_v: number) => void) => {
        _setNewConversationMessageHandler((old) => {
            if (!old) return old;
            const handlerIndex = old.findIndex((f) => f === fn);

            if (handlerIndex === -1) return old;
            old.splice(handlerIndex, 1);
            const newList = [...old];
            return newList;
        });
    };

    const [readNotificationMessageHandler, setReadNotificationMessageHandler] = useState<TReadNotificationsMessageHandler>();

    const [messageFromSocket, setNewMessageFromSocket] = useState<{
        message: string;
        data: any;
    }>({ message: "none", data: "" });

    const SOCKET_EVENTS = {
        newMessages: "newMessages",
        newConversationMessages: "newConversationMessages",
        readNotification: "readNotification",
    };

    const logOut = () => {
        //setShowChat(false);
        _setListUserIdToChat([]);
        logOutUser();
    };

    const _createSocket = (userInfo: TUserInfo) => {
        const mainDomain = process.env.REACT_APP_WS_ENDPOINT === "host" ? `wss://${window.location.host}` : process.env.REACT_APP_WS_ENDPOINT || "";
        const url = `${mainDomain}?userId=${userInfo.id}&userType=${userInfo.type}`;
        if (window.wwgWS === undefined) {
            window.wwgWS = new WebSocket(url);
            const ws = window.wwgWS;
            if (ws !== undefined && ws !== null) {
                ws.onmessage = (event) => {
                    const eventInfo = event.data.split("::");
                    switch (eventInfo[0]) {
                        case SOCKET_EVENTS.newMessages:
                            setNewMessageFromSocket({
                                message: eventInfo[0],
                                data: undefined,
                            });
                            break;
                        case SOCKET_EVENTS.newConversationMessages:
                            setNewMessageFromSocket({
                                message: eventInfo[0],
                                data: eventInfo[1],
                            });
                            break;
                        case SOCKET_EVENTS.readNotification:
                            setNewMessageFromSocket({
                                message: eventInfo[0],
                                data: JSON.parse(decodeURIComponent(eventInfo[1])),
                            });
                            break;
                        default:
                            break;
                    }
                };
                const wsKeepAlive = () => {
                    if (window.wwgWS) {
                        if (window.wwgWS.readyState === 1) {
                            window.wwgWS.send("ping");
                        }
                        setTimeout(() => {
                            wsKeepAlive();
                        }, 7000);
                    }
                };
                ws.onclose = async () => {
                    removeSocket();
                };
                wsKeepAlive();
            }
        }
    };

    useEffect(() => {
        const eventInfo = messageFromSocket.message;

        switch (eventInfo) {
            case SOCKET_EVENTS.newMessages: //triggers everytime we receive a chat message or a notification to show in the notification section
                console.log("angel: new messsage ws: ", eventInfo, " messagefronsocket: ", messageFromSocket, " handlers: ", newMessagesHandler);
                if (newMessagesHandler) newMessagesHandler.forEach((f) => f());
                break;
            case SOCKET_EVENTS.newConversationMessages: //triggers every time a new conversation message is received/sent during an open chat
                // console.log(
                //     "angel: new conversation ws: ",
                //     eventInfo,
                //     messageFromSocket,
                //     newConversationMessageHandler
                // );
                if (newConversationMessageHandler) {
                    const conversationIdFromSocket = Number(messageFromSocket.data);
                    newConversationMessageHandler.forEach((f) => f(conversationIdFromSocket));
                }
                break;
            case SOCKET_EVENTS.readNotification: //triggers every time someone reads a message
                // console.log(
                //     "angel: new read notification ws: ",
                //     eventInfo,
                //     messageFromSocket
                // );
                if (readNotificationMessageHandler) readNotificationMessageHandler(messageFromSocket.data as TWSReadNotificationData);
                break;
            default:
                break;
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messageFromSocket]);

    if (userInfo && userInfo.id && userInfo.id > 0) {
        _createSocket(userInfo);
    }

    const getChats: TGetChats = async () => {
        return await chatServices.getConversations(await getUserTokenId());
    };

    const sendMessage: TSendChat = async (content: string, conversationId: number) => {
        return await chatServices.sendMessage(content, await getUserTokenId(), conversationId);
    };

    const getMessages: TGetChats = async (conversationId: number, lastMessageDateNoTZ?: string) => {
        return await chatServices.getMessages(await getUserTokenId(), conversationId, lastMessageDateNoTZ);
    };

    const startConversation: TGetChatsWithUser = async (withUserId: number) => {
        return await chatServices.startChatting(await getUserTokenId(), withUserId);
    };

    const endConversation = async (withUserId: number) => {
        return await chatServices.endChatting(await getUserTokenId(), withUserId);
    };
    const getChatList: TGetChatList = async (meId: number, pageNumber: number, is_archived: boolean) => {
        const response = await chatServices.chatList(await getUserTokenId(), pageNumber, is_archived);
        return { data: response.chatList };
    };

    const [openCloseMessageCenterHandler, setOpenCloseMessageCenterHandler] = useState<(() => void)[]>([]);
    const openCloseMessageCenter = () => {
        // console.log("angel: openclosemessagecenter ", openCloseMessageCenter);
        if (openCloseMessageCenterHandler.length) {
            openCloseMessageCenterHandler.forEach((mh) => mh());
        }
    };
    const addOpenCloseMessageCenterHandler = (newHandler: () => void) => {
        setOpenCloseMessageCenterHandler((old) => {
            if (old.findIndex((mh) => mh === newHandler) !== -1) return old;
            return [...old, newHandler];
        });
    };

    const removeOpenCloseMessageCenterHandler = (oldHandler: () => void) => {
        setOpenCloseMessageCenterHandler((old) => {
            if (!old) return old;
            const handlerIndex = old.findIndex((f) => f === oldHandler);

            if (handlerIndex === -1) return old;
            old.splice(handlerIndex, 1);
            const newList = [...old];
            return newList;
        });
    };

    const sendToArchive: TSendToArchive = async (conversation_id, setArchive) => {
        await chatServices.sendToArchive(conversation_id, await getUserTokenId(), setArchive);
    };

    /*    useEffect(() => {
            setTimeout(() => {
                checkMessages();
            }, 1000);
            // eslint-disable-next-line        
        }, [])
    */

    //#region notifications popup
    const [showNotificationsPopup, setShowNotificationsPopup] = useState<boolean>(false);
    const [notificationsPopupMessage, setNotificationsPopupMessage] = useState<string>("");
    const [notificationsPopupTitle, setNotificationsPopupTitle] = useState<string>("");
    const showPopup = (message: string, title: string) => {
        setNotificationsPopupMessage(message);
        setNotificationsPopupTitle(title);
        setShowNotificationsPopup(true);
    };
    const notificationsGetLogoURL = (message: TMessage) => {
        if (message.isFromChats) {
            return getImagePath("logo", "fixed", (message.action_data.data as TUserToChatInfo).id + "");
        }
        if (message.action_data.goto) {
            return getImagePath("logo", "fixed", message.action_data.data);
        }
        return ASSETS_ROUTES.IMGS.logo;
    };
    //TODO: Angel - this is a work in progress general message click handler
    /**
     * this is a work in progres general message handler
     * @param message
     */
    const notificationMessageClickHandler = (message: TMessage) => {
        const { onClickAction } = message.action_data;
        if (onClickAction) {
            switch (onClickAction) {
                case "showModal":
                    showPopup(message.action_data.data, message.title);
                    break;

                default:
                    break;
            }
        }
    };

    //#endRegion
    return {
        security: {
            isUserEmailVerified,
            sendEmailVerification: userSendEmailVerification,
            userPhoneNeedsVerification: () => {
                const hasPhone = !!userInfo?.other?.phone;
                const isPhoneVerified = userInfo?.verifications?.phone === true;
                return hasPhone && !isPhoneVerified;
            },
        },
        general: {
            logOut,
        },
        notifications: {
            showPopup,
            hidePopup,
            notificationsPopupTitle,
            showNotificationsPopup,
            notificationsGetLogoURL,
            notificationsPopupMessage,
            notificationMessageClickHandler,
            addNewMessagesHandler,
            removeMessageHandler,
            addNewConversationMessageHandler,
            removeNewConversationMessageHandler,
            setReadNotificationMessageHandler: (v) => setReadNotificationMessageHandler((_prev?: TReadNotificationsMessageHandler) => v),
        },
        chats: {
            openCloseMessageCenter,
            addOpenCloseMessageCenterHandler,
            removeOpenCloseMessageCenterHandler,
            removeUserIdToChat,
            endConversation,
            getChats,
            getMessages,
            sendMessage,
            startConversation,
            //setShowChat,
            listUserIdToChat,
            addUserIdToChat,
            removeSocket,
            getChatList,
            sendToArchive,
        },
    };
};

const ServiceContext = createContext<TUseServiceContext>({
    security: {
        isUserEmailVerified: async () => false,
        sendEmailVerification: () => {},
        userPhoneNeedsVerification: () => false,
    },
    general: {
        logOut: () => {},
    },
    notifications: {
        notificationsPopupTitle: "",
        showPopup: (_v: string, _t: string) => {},
        hidePopup: () => {},
        showNotificationsPopup: false,
        notificationsPopupMessage: "",
        notificationsGetLogoURL: (_v: TMessage) => "",
        notificationMessageClickHandler: (_v: TMessage) => {},
        removeMessageHandler: (_v: () => void) => {},
        addNewMessagesHandler: (_v: (() => void) | undefined) => {},
        removeNewConversationMessageHandler: (_v: (_ci: number) => void) => {},
        addNewConversationMessageHandler: (_v: ((_ci: number) => void) | undefined) => {},
        setReadNotificationMessageHandler: (_v?: (p: TWSReadNotificationData) => void) => {},
    },
    chats: {
        addOpenCloseMessageCenterHandler: (_v: () => void) => {},
        removeOpenCloseMessageCenterHandler: (_v: () => void) => {},
        openCloseMessageCenter: () => {},
        removeUserIdToChat: (_v: number) => {},
        getChats: async (_v: number): Promise<TChatResponse> => ({
            data: {
                messages: [],
                conversationId: -1,
            },
        }),
        sendMessage: async (_v: string, _conversationId: number) => {},
        startConversation: async (_v: number) => ({
            data: { messages: [], conversationId: -1 },
        }),
        endConversation: async (_v: number) => ({
            data: { messages: [], conversationId: -1 },
        }),
        getMessages: async (_v: number) => ({
            data: { messages: [], conversationId: -1 },
        }),
        //setShowChat: (_v: boolean) => {},
        listUserIdToChat: [],
        addUserIdToChat: (_v: TUserToChatInfo) => {},
        removeSocket,
        getChatList: async (_v: number, _pageNumber: number, _is_archived?: boolean): Promise<TChatListResponse> => ({
            data: {
                totalCount: 0,
                unreadTotalCount: 0,
                data: [
                    {
                        conversation_id: 0,
                        content: "",
                        chat_with_user_id: 0,
                        guid: "",
                        name: "",
                        user_type: "",
                        sent_date: "",
                        state: "unread",
                        from_user_logo: "",
                        last_message_from: 0,
                        unread_message_from_sender: false,
                        is_archived: false,
                    },
                ],
            },
        }),
        sendToArchive: async (_conversation_id: number, _v: boolean) => {},
    },
});

export const useServiceContext = () => {
    return useContext(ServiceContext);
};

export const ServiceProvider = (props: any) => {
    const value: TUseServiceContext = useService();

    return (
        <ServiceContext.Provider value={value}>
            {props.children}
            <NotificationsPopup />
        </ServiceContext.Provider>
    );
};

const NotificationsPopup = () => {
    const { notifications } = useServiceContext();

    return (
        <>
            {notifications.showNotificationsPopup && (
                <>
                    <Modal onCloseHandler={notifications.hidePopup} title={notifications.notificationsPopupTitle}>
                        <div className="modal-body text-start border-0">
                            {notifications.notificationsPopupMessage.split("\n").map((m) => (
                                <p>{m}</p>
                            ))}
                        </div>
                    </Modal>
                </>
            )}
        </>
    );
};
